import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { User } from 'src/app/model/settings/user.model';
import { Partner } from 'src/app/model/settings/partner.model';
import { UserService } from 'src/app/service/user.service';
import { scrubSensitive, stripQuery } from 'src/app/util/pii-scrub.util';

// Type-only reference to the SDK's default export — no runtime/bundle impact.
type Mixpanel = (typeof import('mixpanel-browser'))['default'];

/**
 * Canonical Mixpanel event names for the Partner Portal (NEWTON-483).
 * Centralized here so names stay consistent and typo-proof — this union IS the
 * frontend event taxonomy. Keep phrasing aligned with the backend taxonomy
 * (Title Case "Object Action", past tense) — see NEWTON-483-plan.md §6.
 */
export type AnalyticsEvent =
  // auth / access
  | 'Login Succeeded'
  | 'Login Failed'
  | 'Logout'
  | 'Password Reset Requested'
  | 'Password Reset Completed'
  | 'Partner Switched'
  // user administration
  | 'User Invited'
  | 'Invite Accepted'
  | 'User Created'
  | 'User Updated'
  | 'User Deleted'
  // configuration
  | 'Company Settings Updated'
  // store lifecycle
  | 'Store Created'
  | 'Go Live Initiated'
  | 'Store Hibernation Changed'
  | 'Handheld Provisioned'
  | 'Station Provisioned'
  // navigation
  | 'Page Viewed';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  // The SDK is dynamically imported (lazy chunk) and initialized once, on first
  // use, so it stays out of the initial bundle. All public methods await this
  // shared promise, which also preserves call order (identify before track).
  private mixpanel?: Promise<Mixpanel | null>;

  constructor(private userService: UserService) { }

  /**
   * Identify the current user and register partner/role super properties.
   * Called on login (SessionService.start) and on refresh (loadSession).
   * Ids/categoricals only — never PII (no email/name/phone/postal), matching
   * the Sentry policy (NEWTON-470).
   */
  public identify(user: User,
                  partner: Partner | undefined): void {
    if (!environment.enableMixpanel || user?.userId == null) {
      return;
    }

    this.instance().then(mp => {
      if (!mp) {
        return;
      }
      mp.identify(String(user.userId));
      mp.register(this.superProperties(user, partner));
      // Non-PII profile attributes only.
      mp.people.set({
        role_access: user.access,
        is_portal_staff: this.userService.isPortalStaff(user),
      });
    });
  }

  /**
   * Re-register partner super properties when the active partner changes
   * (SessionService.setPartner).
   */
  public updatePartnerContext(partner: Partner | undefined): void {
    if (!environment.enableMixpanel) {
      return;
    }

    this.instance().then(mp => mp?.register({
      partner_id: partner?.id != null ? partner.id : null,
      partner_type: partner?.type != null ? partner.type : null,
    }));
  }

  /**
   * Single choke point for tracking events. Scrubs every property through the
   * shared PII scrubber before send — defense in depth against a caller passing
   * a raw form/model object.
   */
  public track(event: AnalyticsEvent,
               properties?: Record<string, any>): void {
    if (!environment.enableMixpanel) {
      return;
    }

    this.instance().then(mp => mp?.track(event,
                                         properties ? scrubSensitive(properties) : undefined));
  }

  /**
   * Clear identity + super properties on logout (SessionService.destroySession).
   */
  public reset(): void {
    if (!environment.enableMixpanel) {
      return;
    }

    this.instance().then(mp => mp?.reset());
  }

  /**
   * Lazily import + init the SDK once; subsequent calls reuse the same promise.
   * ip:true lets Mixpanel derive geography (city/region/country) from the request
   * IP at ingestion — consistent with what Sentry already surfaces; the raw IP is
   * not exposed as a queryable property, and events remain PII-scrubbed.
   */
  private instance(): Promise<Mixpanel | null> {
    if (!environment.enableMixpanel) {
      return Promise.resolve(null);
    }

    if (!this.mixpanel) {
      this.mixpanel = import('mixpanel-browser').then(mod => {
        const mp = mod.default;
        mp.init(environment.mixpanelToken, {
          api_host: environment.mixpanelApiHost,
          debug: !environment.production,
          persistence: 'localStorage',
          ip: true,
          // The SDK auto-captures $current_url/$referrer/$initial_referrer as the
          // full location.href — and routes like invite-acceptance, reset-password,
          // and upload-page carry email/tokens in the query string. Strip the query
          // from those URL properties (keeping the path for analytics).
          // NOTE: only touch those specific keys — do NOT run a blanket key-based
          // scrub here. The SDK stamps its own reserved props into properties,
          // including the project token under the key `token`; redacting that
          // makes Mixpanel reject the event (status 0). Our own event properties
          // are already scrubbed in track() before they reach the SDK.
          hooks: {
            before_send_events: (payload: { event: string; properties: Record<string, any> }) => {
              const props = payload.properties || {};
              for (const key of ['$current_url', '$referrer', '$initial_referrer']) {
                if (typeof props[key] === 'string') {
                  props[key] = stripQuery(props[key]);
                }
              }
              return payload;
            },
          },
        });
        return mp;
      }).catch(err => {
        // Degrade silently if the lazy chunk fails to load or init throws —
        // returning null (never rejecting) keeps analytics no-op and avoids
        // unhandled rejections being captured as Sentry noise on every call.
        console.error('Mixpanel init failed', err);
        return null;
      });
    }

    return this.mixpanel;
  }

  private superProperties(user: User,
                          partner: Partner | undefined): Record<string, any> {
    return {
      partner_id: partner?.id != null ? partner.id : null,
      partner_type: partner?.type != null ? partner.type : null,
      is_portal_staff: this.userService.isPortalStaff(user),
      role_access: user.access,
      environment: environment.name,
      app_version: environment.version,
    };
  }
}
